import { ulid } from 'ulid';
import { useMutation } from '@tanstack/react-query';
import { CreateMessageMutationInput } from './types';
import { useOptimisticCreateMessage } from './optimistic';
import { createMessage, useGraphqlClient } from '../graphql';
import { messagingMutationKeys } from './mutationKeys';

export const useCreateMessage = () => {
  const gqlClient = useGraphqlClient();
  const { optimisticCreate, onOptimisticCreateError, onOptimisticCreateSuccess } = useOptimisticCreateMessage();

  const mutation = useMutation({
    mutationKey: messagingMutationKeys.createMessage,
    mutationFn: ({ createMessageInput }: CreateMessageMutationInput) => {
      return createMessage(gqlClient, createMessageInput);
    },
    onMutate: async ({ createMessageInput, senderID, notOptimistic, pendingFiles, conversationQueryKey }) => {
      const { message, conversationID } = createMessageInput;
      if (!conversationID || notOptimistic) return;

      return await optimisticCreate(conversationID, conversationQueryKey, {
        id: ulid(),
        sender: senderID,
        changeToken: ulid(),
        attachments: pendingFiles,
        __typename: 'FreeformMessage',
        conversationID: conversationID,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        message: message.freeform?.content || '',
        suggestedResponses: message.freeform?.suggestedResponses,
        hasOnboardingReminder: Boolean(message.hasOnboardingReminder),
      });
    },
    onSuccess: async (payload, input) => {
      input.onSuccess?.(payload);
    },
    onError: (_, __, context) => onOptimisticCreateError(context),
    onSettled: (_, __, ___, context) => {
      return onOptimisticCreateSuccess(context);
    }
  });

  return { createMessage: mutation };
};
